import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { useTranslation } from "react-i18next";
import Modal from "@material-ui/core/Modal/Modal";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { LinkWrapper } from "../applicantDetails/applicantDetails.styled";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Api } from "services/api/api.service";
import {
  EMPLOYEE_CATEGORY_OPTIONS,
  EMPLOYMENT_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  SALARY_OPTIONS,
  WorkerProfileData,
} from "model/OrganizationalStructure";
import { formatDuration } from "utils/utils";
import {
  ActionContainer,
  Column,
  ImageContainer,
  OrgStructureWorkerModalBody,
  SimpleRow,
  WorkerImage,
} from "./organizationalStructure.styled";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { ButtonComponent } from "../general/button/button.component";
import { TextInputWithLabel } from "../general/textInput/textInput.component";
import {
  MultiSelectOption,
  MultiSelectWithLabel,
} from "../general/multiSelectWithLabel/multiSelectWithLabel.component";
import { DropdownWithLabel } from "../general/dropDownWithLabel/dropdownWithLabel.component";
import { DatePickerWithLabel } from "../general/datePickerWithLabel/datePickerWithLabel.component";
import { InfoModal } from "../general/infoModal/infoModal.component";
import { taxTableOptions } from "../salary/updateWorkerSalaryModal.component";

type Props = {
  setShowWorkerProfile: React.Dispatch<React.SetStateAction<boolean>>;
  showWorkerProfile: boolean;
  worker: number;
  setClickedWorker: React.Dispatch<React.SetStateAction<number | undefined>>;
};

interface Worker {
  externalId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  personalIdentityNumber: string;
  description: string;
  isRegistered: boolean;
  workPercentage: number;
  workGroups: Array<{ id: number }>;
  employmentInfo: {
    current: {
      employmentType: string;
      salaryType: string;
      salary: number;
      startDate: string;
      endDate: string | null;
      taxTable: number;
      paymentType: string;
      role: string;
      employeeCategory: string;
      employeeNumber: number | undefined;
    };
    endDate: string;
  };
  skills: any[];
  stats: Array<{ stat: string; value: string | number }>;
  documents: Array<{ fileName: string; downloadUrl: string }>;
}

export const WorkerProfile: React.FC<Props> = ({
  setShowWorkerProfile,
  showWorkerProfile,
  worker: workerId,
  setClickedWorker,
}) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [state, organizationalStructureActions] =
    useOrganizationalStructureStore();

  const [worker, setWorker] = useState<Worker | null>(null);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState<WorkerProfileData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    personalIdentityNumber: "",
    employmentType: undefined,
    workGroupIds: [],
    salaryType: undefined,
    salary: undefined,
    startDate: undefined,
    endDate: null,
    description: "",
    skills: [],
    stats: [],
    documents: [],
    workPercentage: 1,
    taxTable: 36,
    paymentType: "",
    role: "",
    employeeCategory: "",
    employeeNumber: undefined,
  });

  const [loading, setLoading] = useState(false);

  const isTraineeEmployee =
    (formData.employmentType ?? "").toLowerCase() === "trainee";

  const isSimflexityEmployee = formData.employmentType === "SimflexityEmployee";

  const originalDataRef = useRef<{
    formData: WorkerProfileData;
    selectedTeams: string[];
  } | null>(null);

  const workerImage = worker?.externalId
    ? companyState.companyWorkerImages?.[worker.externalId]
    : undefined;

  const workgroupOptions: MultiSelectOption[] = useMemo(() => {
    return (
      state.structure?.entities?.flatMap(
        (entity) =>
          entity.units
            ?.filter((unit) => unit.name !== "Unhandled")
            ?.flatMap(
              (unit) =>
                unit.workGroups?.map((team) => ({
                  value: team.id.toString(),
                  label: unit.name.toString() + " - " + team.name,
                })) || []
            ) || []
      ) || []
    );
  }, [state.structure]);

  const hasChanges = useMemo(() => {
    if (!originalDataRef.current) return false;

    const { formData: originalFormData, selectedTeams: originalSelectedTeams } =
      originalDataRef.current;

    const teamsChanged =
      selectedTeams.length !== originalSelectedTeams.length ||
      selectedTeams.some((team) => !originalSelectedTeams.includes(team));

    const formDataChanged = Object.keys(originalFormData).some((key) => {
      if (key === "workGroupIds") return false;

      const originalValue = originalFormData[key as keyof WorkerProfileData];
      const currentValue = formData[key as keyof WorkerProfileData];

      if (Array.isArray(originalValue) && Array.isArray(currentValue)) {
        return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
      }

      return originalValue !== currentValue;
    });

    return teamsChanged || formDataChanged;
  }, [formData, selectedTeams]);

  const employmentOptions = EMPLOYMENT_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const salaryOptions = SALARY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const paymentTypeOptions = PAYMENT_TYPE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const employeeCategoryOptions = EMPLOYEE_CATEGORY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const downloadFile = (url: string) => {
    window.location.href = url;
  };

  const updateFormData = useCallback((updates: Partial<WorkerProfileData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const getStatValue = (statName: string): string | number => {
    const stat = formData.stats.find((s) => s.stat === statName);
    return stat?.value ?? 0;
  };

  const handleClose = () => {
    if (!error) {
      setShowWorkerProfile(false);
      setDeleting(false);
      setClickedWorker(undefined);
    } else {
      setError(false);
      setShowInfo(false);
    }
  };

  const stopScrollBubbling = (e: React.WheelEvent | React.TouchEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (!companyState.company?.id || !workerId) return;

    Api()
      .company.organizationalStructure.fetchWorkerProfile(
        companyState.company.id,
        workerId
      )
      .then((res) => {
        setWorker(res.data);
        setIsRegistered(res.data.isRegistered);
      })
      .catch((err) => console.error("Failed to fetch worker profile:", err));
  }, [companyState.company?.id, workerId]);

  useEffect(() => {
    if (!worker) return;

    const workgroupIds =
      worker.workGroups?.map((workgroup) => workgroup.id.toString()) || [];

    const initialFormData = {
      firstName: worker.firstName || "",
      lastName: worker.lastName || "",
      email: worker.email || "",
      phoneNumber: worker.phoneNumber || "",
      personalIdentityNumber: worker.personalIdentityNumber || "",
      employmentType: worker.employmentInfo?.current?.employmentType,
      workGroupIds: workgroupIds.map((id) => Number(id)),
      salaryType: worker.employmentInfo?.current?.salaryType,
      salary: worker.employmentInfo?.current?.salary,
      startDate: worker.employmentInfo?.current?.startDate,
      endDate: worker.employmentInfo.current.endDate?.includes("9999-12-31")
        ? null
        : worker.employmentInfo?.current?.endDate,
      description: worker.description || "",
      skills: worker.skills || [],
      stats:
        worker.stats?.map((stat) => ({
          ...stat,
          value:
            typeof stat.value === "string"
              ? parseFloat(stat.value) || 0
              : stat.value,
        })) || [],
      documents:
        worker.documents?.map((doc) => ({
          ...doc,
          fileSize: 0,
        })) || [],
      workPercentage: worker.workPercentage || 1,
      taxTable:
        worker.employmentInfo.current.taxTable === 0
          ? 36
          : worker.employmentInfo.current.taxTable,
      paymentType: worker.employmentInfo.current.paymentType,
      role: worker.employmentInfo.current.role,
      employeeCategory: worker.employmentInfo.current.employeeCategory,
      employeeNumber: worker.employmentInfo.current.employeeNumber,
    };

    setSelectedTeams(workgroupIds);
    setFormData(initialFormData);

    originalDataRef.current = {
      formData: { ...initialFormData },
      selectedTeams: [...workgroupIds],
    };
  }, [worker]);

  const handleSaveChanges = () => {
    setLoading(true);
    const { skills, stats, documents, ...data } = formData;

    if (formData.salaryType !== "Monthly") {
      delete (data as any).workPercentage;
    }

    if (companyState.company) {
      organizationalStructureActions
        .updateWorker(companyState.company.id, workerId, data)
        .then(() => {
          if (originalDataRef.current) {
            originalDataRef.current = {
              formData: { ...formData },
              selectedTeams: [...selectedTeams],
            };
          }
          setShowInfo(true);
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setShowInfo(true);
          setLoading(false);
          console.log("error when updating worker");
        });
    }
  };

  const handleRemoveWorker = () => {
    setLoading(true);
    if (companyState.company) {
      organizationalStructureActions
        .deleteWorker(companyState.company.id, workerId)
        .then(() => {
          setDeleting(true);
          setShowInfo(true);
          setLoading(false);
        })
        .catch(() => {
          setDeleting(false);
          setError(true);
          setShowInfo(true);
          setLoading(false);
          console.log("error when deleting worker");
        });
    }
  };

  const updateWorkGroupIds = useCallback(() => {
    updateFormData({
      workGroupIds: selectedTeams.map((team) => Number(team)),
    });
  }, [selectedTeams, updateFormData]);

  useEffect(() => {
    updateWorkGroupIds();
  }, [updateWorkGroupIds]);

  if (!worker) return null;

  return (
    <>
      <Modal open={showWorkerProfile} onClose={handleClose}>
        <OrgStructureWorkerModalBody
          style={{
            maxHeight: "calc(100vh - 64px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <LinkWrapper>
            <CustomIcon
              color={Color.DarkGrey}
              onClick={handleClose}
              name="cross"
            >
              {t("General.Close")}
            </CustomIcon>
          </LinkWrapper>

          <SimpleRow
            style={{ alignItems: "flex-start", gap: "24px", marginBottom: 32 }}
          >
            <ImageContainer>
              {workerImage && workerImage !== "no-image" ? (
                <WorkerImage src={workerImage} />
              ) : (
                <CustomIcon
                  className="icon"
                  name="person"
                  size="130px"
                  color={Color.White}
                  backgroundColor={Color.LightGrey03}
                  padding="25px"
                />
              )}
            </ImageContainer>
            <Column style={{ flex: 1 }}>
              <SimpleRow
                style={{
                  alignItems: "flex-start",
                  gap: "40px",
                  marginBottom: 16,
                  marginRight: 60,
                }}
              >
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  fontSize={FontSize.H2}
                  style={{ margin: 0, flex: 1 }}
                >
                  {`${formData.firstName} ${formData.lastName}`}
                </Text>
                <SimpleRow style={{ gap: "24px", alignItems: "center" }}>
                  <SimpleRow style={{ alignItems: "center", gap: "6px" }}>
                    <CustomIcon
                      name="star-filled"
                      size="18px"
                      color={Color.DarkGrey}
                    />
                    <Column style={{ gap: "0px" }}>
                      <Text
                        fontSize={FontSize.Standard}
                        fontFamily={FontFamily.MontserratSemiBold}
                        style={{ margin: 0, lineHeight: 1.2 }}
                      >
                        {`${getStatValue("Rating")}/5`}
                      </Text>
                      <Text
                        fontSize={FontSize.Small}
                        fontFamily={FontFamily.MontserratRegular}
                        style={{
                          margin: 0,
                          color: Color.DarkGrey,
                          lineHeight: 1.2,
                        }}
                      >
                        {`${getStatValue("AmountOfRatings")} ratings`}
                      </Text>
                    </Column>
                  </SimpleRow>

                  <SimpleRow style={{ alignItems: "center", gap: "6px" }}>
                    <CustomIcon
                      name="clock"
                      size="18px"
                      color={Color.DarkGrey}
                    />
                    <Column style={{ gap: "0px" }}>
                      <Text
                        fontFamily={FontFamily.MontserratSemiBold}
                        fontSize={FontSize.Standard}
                        style={{ margin: 0, lineHeight: 1.2 }}
                      >
                        {(() => {
                          const hours = getStatValue("AmountOfHoursWorked");
                          return hours ? formatDuration(hours.toString()) : "0";
                        })()}
                      </Text>
                      <Text
                        fontFamily={FontFamily.MontserratRegular}
                        fontSize={FontSize.Small}
                        style={{
                          margin: 0,
                          color: Color.DarkGrey,
                          lineHeight: 1.2,
                        }}
                      >
                        {t("OrganizationalStructure.WorkedHours")}
                      </Text>
                    </Column>
                  </SimpleRow>
                </SimpleRow>
              </SimpleRow>
              <Text
                fontFamily={FontFamily.MontserratRegular}
                fontSize={FontSize.Standard}
                style={{
                  margin: 0,
                  lineHeight: 1.6,
                  color: Color.DarkGrey,
                  maxWidth: "600px",
                }}
              >
                {formData.description ||
                  t("OrganizationalStructure.NoDescription")}
              </Text>
            </Column>
          </SimpleRow>

          <SimpleRow>
            <Column style={{ marginRight: 10, width: "60%" }}>
              <Text fontFamily={FontFamily.MontserratSemiBold}>
                {t("OrganizationalStructure.Details")}
              </Text>
              <TextInputWithLabel
                disabled={isRegistered}
                label={t("OrganizationalStructure.FirstName")}
                required
                value={formData.firstName}
                fontSize={FontSize.Standard}
                onChange={(e) => updateFormData({ firstName: e.target.value })}
                marginBottom={15}
              />
              <TextInputWithLabel
                disabled={isRegistered}
                label={t("OrganizationalStructure.LastName")}
                required
                value={formData.lastName}
                onChange={(e) => updateFormData({ lastName: e.target.value })}
                marginBottom={15}
              />
              <TextInputWithLabel
                disabled={isRegistered}
                label={t("OrganizationalStructure.Email")}
                value={formData.email}
                onChange={(e) => updateFormData({ email: e.target.value })}
                type="email"
                marginBottom={15}
              />
              <TextInputWithLabel
                disabled={isRegistered}
                label={t("OrganizationalStructure.Phone")}
                required
                value={formData.phoneNumber}
                onChange={(e) =>
                  updateFormData({ phoneNumber: e.target.value })
                }
                type="phone"
                marginBottom={15}
              />
              <TextInputWithLabel
                disabled={isRegistered}
                label={t("OrganizationalStructure.PersonalIdentityNumber")}
                value={formData.personalIdentityNumber}
                onChange={(e) =>
                  updateFormData({ personalIdentityNumber: e.target.value })
                }
                marginBottom={10}
              />
              <div
                onWheelCapture={stopScrollBubbling}
                onTouchMoveCapture={stopScrollBubbling}
              >
                <MultiSelectWithLabel
                  label={t("OrganizationalStructure.Team/s")}
                  required
                  options={workgroupOptions}
                  values={selectedTeams}
                  onChange={setSelectedTeams}
                  placeholder={t("OrganizationalStructure.NotAssigned")}
                  maxDisplayTags={3}
                  showSelectAll
                />
              </div>
            </Column>

            <Column style={{ marginRight: 10, width: "60%" }}>
              <Text fontFamily={FontFamily.MontserratSemiBold}>
                {t("OrganizationalStructure.CurrentEmployment")}
              </Text>
              <TextInputWithLabel
                disabled
                label={t("OrganizationalStructure.Role")}
                value={formData.role ?? "-"}
                fontSize={FontSize.Standard}
                onChange={(e) => updateFormData({ role: e.target.value })}
                marginBottom={15}
              />
              <DropdownWithLabel
                disabled
                label={t("OrganizationalStructure.EmploymentType")}
                options={employmentOptions}
                value={formData.employmentType}
                onChange={(value) => updateFormData({ employmentType: value })}
                placeholder={t("OrganizationalStructure.ChooseEmploymentType")}
              />
              {!isTraineeEmployee && !isSimflexityEmployee && (
                <>
                  <DropdownWithLabel
                    label={t("OrganizationalStructure.EmployeeCategory")}
                    options={employeeCategoryOptions}
                    value={formData.employeeCategory}
                    onChange={(value) =>
                      updateFormData({ employeeCategory: value })
                    }
                    required
                    disabled
                  />
                  <TextInputWithLabel
                    maxLength={50}
                    label={t("OrganizationalStructure.EmployeeNumber")}
                    value={formData.employeeNumber?.toString()}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employeeNumber: Number(e.target.value),
                      })
                    }
                    marginBottom={10}
                    marginRight={10}
                    disabled
                    required
                  />
                </>
              )}
              {!isTraineeEmployee && (
                <DropdownWithLabel
                  disabled
                  label={t("OrganizationalStructure.SalaryModel")}
                  options={salaryOptions}
                  value={formData.salaryType}
                  onChange={(value) => updateFormData({ salaryType: value })}
                  placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                />
              )}
              {!isTraineeEmployee && (
                <DropdownWithLabel
                  label={t("ProfileCompany.PaymentSolution")}
                  options={paymentTypeOptions}
                  value={formData.paymentType}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      paymentType: value,
                    })
                  }
                  disabled
                />
              )}
              {!isTraineeEmployee && (
                <TextInputWithLabel
                  disabled
                  label={
                    formData.salaryType === "Monthly"
                      ? t("OrganizationalStructure.Month")
                      : t("OrganizationalStructure.Hour")
                  }
                  value={formData.salary?.toString() || ""}
                  onChange={(e) =>
                    updateFormData({ salary: parseFloat(e.target.value) || 0 })
                  }
                  placeholder={t("OrganizationalStructure.AddSalary")}
                  type="number"
                  marginBottom={10}
                />
              )}
              <DatePickerWithLabel
                disabled
                label={t("OrganizationalStructure.StartDate")}
                value={formData.startDate ? new Date(formData.startDate) : null}
                onChange={(date) =>
                  updateFormData({ startDate: formatDate(date) })
                }
                placeholder={t("OrganizationalStructure.ChooseDate")}
                dateFormat="YYYY-MM-DD"
              />
              {formData.endDate !== null && (
                <DatePickerWithLabel
                  disabled
                  label={t("OrganizationalStructure.EndDate")}
                  value={formData.endDate ? new Date(formData.endDate) : null}
                  onChange={(date) =>
                    updateFormData({ endDate: formatDate(date) })
                  }
                  placeholder={t("OrganizationalStructure.ChooseDate")}
                  dateFormat="YYYY-MM-DD"
                />
              )}
              {formData.salaryType === "Monthly" && (
                <TextInputWithLabel
                  disabled
                  label={t("OrganizationalStructure.WorkPercentage")}
                  value={
                    formData.workPercentage
                      ? (formData.workPercentage * 100).toString() + "%"
                      : ""
                  }
                  onChange={(e) => {
                    updateFormData({
                      workPercentage: e.target.value
                        ? parseFloat(e.target.value) / 100
                        : 0,
                    });
                  }}
                />
              )}
              {!isTraineeEmployee && !isSimflexityEmployee && (
                <DropdownWithLabel
                  disabled
                  label={t("OrganizationalStructure.TaxTable")}
                  options={taxTableOptions}
                  value={String(formData.taxTable)}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      taxTable: Number(value),
                    })
                  }
                  placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                  width={250}
                />
              )}
            </Column>

            <Column style={{ marginRight: 10, width: "60%" }}>
              <Text fontFamily={FontFamily.MontserratSemiBold}>
                {t("OrganizationalStructure.Documents")}
              </Text>
              {formData.documents.length > 0 ? (
                formData.documents.map((document) => (
                  <SimpleRow
                    key={document.fileName}
                    onClick={() => downloadFile(document.downloadUrl)}
                    style={{ cursor: "pointer" }}
                  >
                    <Text
                      fontFamily={FontFamily.MontserratRegular}
                      fontSize={FontSize.Small}
                      style={{ marginBottom: 0 }}
                    >
                      {document.fileName}
                    </Text>
                    <CustomIcon
                      name="download"
                      size="16px"
                      color={Color.Black}
                      style={{ marginLeft: 10, marginTop: 10 }}
                    />
                  </SimpleRow>
                ))
              ) : (
                <Text
                  fontFamily={FontFamily.MontserratRegular}
                  fontSize={FontSize.Small}
                  style={{ marginBottom: 0 }}
                >
                  {t("OrganizationalStructure.NoDocumentsUploaded")}
                </Text>
              )}
            </Column>
          </SimpleRow>

          <ActionContainer style={{ margin: "0 auto" }}>
            <ButtonComponent
              size="small"
              onClick={handleRemoveWorker}
              marginTop={10}
              marginBottom={20}
              marginRight={10}
              disabled={loading}
            >
              {t("OrganizationalStructure.RemoveWorker")}
            </ButtonComponent>
            <ButtonComponent
              size="small"
              onClick={handleSaveChanges}
              action
              marginTop={10}
              marginBottom={20}
              disabled={loading || !hasChanges}
            >
              {t("OrganizationalStructure.SaveChanges")}
            </ButtonComponent>
          </ActionContainer>
        </OrgStructureWorkerModalBody>
      </Modal>

      {showInfo && (
        <InfoModal
          isOpen={showInfo}
          title={
            error
              ? t("OrganizationalStructure.SomethingWentWrong")
              : deleting
              ? t("OrganizationalStructure.Deleted")
              : t("OrganizationalStructure.Updated")
          }
          message={
            error
              ? t("OrganizationalStructure.SomethingWentWrongInfo")
              : deleting
              ? t(`OrganizationalStructure.SuccessfullyDeleted`)
              : t(`OrganizationalStructure.SuccessfullyUpdated`)
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={handleClose}
          onCancel={handleClose}
        />
      )}
    </>
  );
};
